import { PermissionSet, useBusinessContext, usePermission } from '@context'
import {
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useDismiss,
  useFloating,
  useInteractions
} from '@floating-ui/react'
import {
  InlineEditableEditIcon,
  InlineEditableText,
  InlineEditableValue
} from '@root/components/InlineEditableField/InlineEditableField'
import { ContactFormModal } from '@root/pages/ContactsPage/ContactFormModal/ContactFormModal'
import { ContactType, fetchContact, IContact } from '@root/query'
import { useScreen } from '@root/utils'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import React, { useMemo, useRef, useState } from 'react'
import { FaMagic } from 'react-icons/fa'
import { FaPenToSquare } from 'react-icons/fa6'
import ContactsDropdown from '@root/pages/SettingsPage/sections/AutomationSettings/AccountingRule/Form/ContactSelector/ContactsDropdown'
import {
  ContactPendingNewCaption,
  ContactPendingNewCaptionIcon,
  SuggestionContactActionButton
} from '../styles'

const MAX_HEIGHT_DROPDOWN = 300
const MIN_DESKTOP_DROPDOWN_WIDTH = 340

interface SuggestionCardContactSelectorProps {
  value: number | null | undefined
  onChange: (contactId: number | null | undefined) => void
  fallbackContactName?: string | null
  /** No `contact` id on suggestion, only `contact_name` — show “Luodaan uusi kontakti” under the field. */
  showPendingNewContactCaption?: boolean
  pendingNewContactLabel?: string
  /** User picked another contact id than `suggestion.contact` — show “Käytä ehdotettua kontaktia?”. */
  showUseSuggestedContactAction?: boolean
  useSuggestedContactLabel?: string
  onUseSuggestedContact?: () => void
  editable?: boolean
  title?: string
  minWidth?: number
  ValueComponent?: typeof InlineEditableValue
  TextComponent?: typeof InlineEditableText
}

export const SuggestionCardContactSelector: React.FC<SuggestionCardContactSelectorProps> = ({
  value,
  onChange,
  fallbackContactName,
  showPendingNewContactCaption = false,
  pendingNewContactLabel,
  showUseSuggestedContactAction = false,
  useSuggestedContactLabel,
  onUseSuggestedContact,
  editable = true,
  title,
  minWidth = 180,
  ValueComponent = InlineEditableValue,
  TextComponent = InlineEditableText
}) => {
  const { slug } = useBusinessContext()
  const canEditContacts = usePermission(PermissionSet.CONTACT_EDIT)
  const { isMobile } = useScreen()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [selectedContact, setSelectedContact] = useState<IContact | undefined>(undefined)
  const [isContactFormOpen, setIsContactFormOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const { data: selectedContactData } = useQuery({
    queryKey: [slug, 'contacts', value],
    queryFn: () => fetchContact({ slug, contactId: value as number }),
    enabled: typeof value === 'number',
    staleTime: 60 * 1000,
    placeholderData: keepPreviousData
  })

  const displayName = selectedContactData?.name || fallbackContactName || '-'
  const useSuggestedContactQuestion =
    useSuggestedContactLabel && useSuggestedContactLabel.trim().endsWith('?')
      ? useSuggestedContactLabel
      : `${useSuggestedContactLabel ?? ''}?`
  const createDefaults = useMemo(
    () =>
      selectedContact
        ? undefined
        : {
            type: ContactType.UNSET,
            name: fallbackContactName || ''
          },
    [fallbackContactName, selectedContact]
  )

  const { context, refs } = useFloating({
    open: isDropdownOpen,
    onOpenChange: open => {
      setIsDropdownOpen(open)
      if (!open && !isContactFormOpen) {
        triggerRef.current?.focus()
      }
    },
    whileElementsMounted: autoUpdate,
    placement: isMobile ? 'bottom' : 'bottom-start',
    middleware: [
      offset(0),
      flip(),
      shift(),
      size(
        {
          apply: ({ availableWidth, rects, elements }) => {
            Object.assign(elements.floating.style, {
              width: isMobile
                ? `${availableWidth}px`
                : `${Math.max(rects.reference.width, MIN_DESKTOP_DROPDOWN_WIDTH)}px`,
              maxHeight: `${MAX_HEIGHT_DROPDOWN}px`
            })
          }
        },
        [isMobile]
      )
    ]
  })

  const dismiss = useDismiss(context)
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss])

  const setTriggerReference = (node: HTMLButtonElement | null) => {
    triggerRef.current = node
    refs.setReference(node)
  }

  const handleContactSelect = (contactId: number) => {
    onChange(contactId)
    setIsDropdownOpen(false)
    triggerRef.current?.focus()
  }

  const onEditContact = (contact: IContact) => {
    if (!canEditContacts) return
    setSelectedContact(contact)
    setIsContactFormOpen(true)
    setIsDropdownOpen(false)
  }

  const onCreateContact = () => {
    if (!canEditContacts) return
    setSelectedContact(undefined)
    setIsContactFormOpen(true)
    setIsDropdownOpen(false)
  }

  const onContactFormClose = () => {
    setIsContactFormOpen(false)
    triggerRef.current?.focus()
  }

  const onContactFormCreation = (contact: IContact) => {
    onChange(contact.id)
    setSelectedContact(undefined)
    setIsContactFormOpen(false)
    triggerRef.current?.focus()
  }

  if (!editable) {
    return <TextComponent>{displayName}</TextComponent>
  }

  return (
    <>
      <ValueComponent
        ref={setTriggerReference}
        type="button"
        $minWidth={minWidth}
        title={title}
        onClick={() => setIsDropdownOpen(prev => !prev)}
        {...getReferenceProps()}
      >
        <TextComponent>{displayName}</TextComponent>
        <InlineEditableEditIcon>
          <FaPenToSquare />
        </InlineEditableEditIcon>
      </ValueComponent>

      {showPendingNewContactCaption && pendingNewContactLabel && (
        <ContactPendingNewCaption aria-live="polite">
          <ContactPendingNewCaptionIcon>
            <FaMagic aria-hidden />
          </ContactPendingNewCaptionIcon>
          <span>{pendingNewContactLabel}</span>
        </ContactPendingNewCaption>
      )}

      {isDropdownOpen && (
        <ContactsDropdown
          context={context}
          refs={refs}
          getFloatingProps={getFloatingProps}
          selectedIds={typeof value === 'number' ? [value] : []}
          onContactSelect={handleContactSelect}
          onEditContact={canEditContacts ? onEditContact : undefined}
          onCreateContact={canEditContacts ? onCreateContact : undefined}
        />
      )}

      {showUseSuggestedContactAction && onUseSuggestedContact && useSuggestedContactLabel && (
        <SuggestionContactActionButton type="button" onClick={onUseSuggestedContact}>
          {useSuggestedContactQuestion}
        </SuggestionContactActionButton>
      )}

      <ContactFormModal
        contact={selectedContact}
        createDefaults={createDefaults}
        isVisible={isContactFormOpen}
        handleOnClose={onContactFormClose}
        onCreate={onContactFormCreation}
      />
    </>
  )
}
